import { el } from '../../core/dom.js';
import { currentTheme, toggleTheme } from '../../core/theme.js';
import { NEXT_STEPS, WORKSPACE_STEPS } from './model.js';

function backIcon() {
  return el('svg', {
    'aria-hidden': 'true',
    class: 'auth-barw',
    width: '16',
    height: '16',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '1.75',
    'stroke-linecap': 'square',
  },
  el('path', { d: 'M19 12H5M11 6l-6 6 6 6' }));
}

function themeToggle() {
  const light = currentTheme() === 'light';
  return el('button', {
    type: 'button',
    class: 'auth-tgl',
    'aria-label': light ? 'Switch to dark mode' : 'Switch to light mode',
    onClick() {
      toggleTheme();
      const next = currentTheme() === 'light';
      const btn = document.querySelector('.auth-tgl');
      if (!btn) return;
      btn.setAttribute('aria-label', next ? 'Switch to dark mode' : 'Switch to light mode');
      btn.replaceChildren(
        el('span', { 'aria-hidden': 'true', class: 'auth-tgl-ico' }, next ? '☾' : '☀'),
        next ? 'Dark' : 'Light',
      );
    },
  },
  el('span', { 'aria-hidden': 'true', class: 'auth-tgl-ico' }, light ? '☾' : '☀'),
  light ? 'Dark' : 'Light');
}

function brandRow() {
  return el('div', { class: 'auth-brand-row' },
    el('div', { class: 'auth-brand-left' },
      el('a', {
        class: 'auth-tgl auth-back',
        href: '/',
        'aria-label': 'Back to website',
        title: 'Back to website',
      }, backIcon()),
      el('a', { class: 'auth-logo', href: '/', 'aria-label': 'Bonnie Ai home' },
        el('img', {
          src: '/site/assets/bonnie-mark.png',
          alt: '',
          width: '28',
          height: '28',
        }),
        el('span', { class: 'auth-word' }, 'Bonnie ', el('em', null, 'Ai')))),
    themeToggle());
}

function googleIcon() {
  return el('img', {
    src: '/v2/assets/google.svg',
    alt: '',
    width: '18',
    height: '18',
    class: 'auth-sso-ico',
  });
}

function microsoftIcon() {
  return el('svg', {
    'aria-hidden': 'true',
    width: '18',
    height: '18',
    viewBox: '0 0 24 24',
  },
  el('path', { d: 'M2 2h9.5v9.5H2z', fill: '#f25022' }),
  el('path', { d: 'M12.5 2H22v9.5h-9.5z', fill: '#7fba00' }),
  el('path', { d: 'M2 12.5h9.5V22H2z', fill: '#00a4ef' }),
  el('path', { d: 'M12.5 12.5H22V22h-9.5z', fill: '#ffb900' }));
}

function githubIcon() {
  return el('img', {
    src: '/v2/assets/github.svg',
    alt: '',
    width: '18',
    height: '18',
    class: 'auth-sso-ico auth-sso-ico-gh',
  });
}

/**
 * @param {'login'|'signup'} intent
 * @param {(provider: string) => void} onStart
 */
export function socialRow(intent, onStart) {
  const items = [
    { id: 'google', label: 'Google', icon: googleIcon() },
    { id: 'microsoft', label: 'Microsoft', icon: microsoftIcon() },
    { id: 'github', label: 'GitHub', icon: githubIcon() },
  ];
  return el('div', { class: 'auth-ssorow' },
    items.map((item) => el('button', {
      type: 'button',
      class: 'auth-sso',
      'aria-label': `Continue with ${item.label}`,
      'data-track': `oauth_${item.id}`,
      onClick() { onStart(item.id); },
    }, item.icon, el('span', null, item.label))));
}

function stepList(steps) {
  return el('div', { class: 'auth-steps-wrap' },
    el('div', { class: 'auth-rail', 'aria-hidden': 'true' }, el('i', null)),
    el('ol', { class: 'auth-steps' },
      steps.map((step, index) => el('li', { class: `auth-step${index === 0 ? ' is-first' : ''}` },
        el('span', { class: 'auth-num' }, step.n),
        el('span', { class: 'auth-step-body' },
          el('span', { class: 'auth-step-title' }, step.title),
          el('span', { class: 'auth-step-copy' }, step.copy),
          el('span', { class: 'auth-step-tag lbl' }, step.tag))))));
}

/**
 * @param {{ variant?: 'default'|'workspace', kicker?: string, title?: string, lead?: string, children: Node|Node[], foot?: Node|null, error?: Node|null }} opts
 */
export function authShell(opts) {
  const steps = opts.variant === 'workspace' ? WORKSPACE_STEPS : NEXT_STEPS;
  return el('div', { class: 'auth-page' },
    el('div', { class: 'auth-orb auth-orb-a', 'aria-hidden': 'true' }),
    el('div', { class: 'auth-orb auth-orb-b', 'aria-hidden': 'true' }),
    el('section', { class: 'auth-formpane', 'aria-labelledby': 'auth-h' },
      brandRow(),
      el('div', { class: 'auth-form-center' },
        el('div', { class: 'auth-hero' },
          opts.kicker ? el('span', { class: 'auth-kicker lbl' }, opts.kicker) : null,
          el('h1', { id: 'auth-h', class: 'auth-h1' }, opts.title || 'Welcome ', opts.titleEm
            ? el('em', null, opts.titleEm)
            : null),
          opts.lead ? el('p', { class: 'auth-lead' }, opts.lead) : null),
        opts.error || null,
        opts.children,
        opts.foot || null),
      el('div', { class: 'auth-copy lbl' }, '© 2026 Bonnie')),
    el('aside', { class: 'auth-story', 'aria-labelledby': 'auth-next-h' },
      el('img', {
        class: 'auth-bigmark',
        src: '/site/assets/bonnie-mark.png',
        alt: '',
        'aria-hidden': 'true',
      }),
      el('div', { class: 'auth-story-head' },
        el('span', { class: 'lbl auth-story-kicker' }, 'What happens next'),
        el('h2', { id: 'auth-next-h', class: 'auth-story-h' },
          'Three steps to ',
          el('em', null, "Bonnie's first take."))),
      stepList(steps)));
}

/** @param {string} message */
export function errorBox(message) {
  if (!message) return el('div', { class: 'auth-error', hidden: true });
  return el('div', { class: 'auth-error', role: 'alert' }, message);
}

/**
 * @param {HTMLElement} mount
 * @param {string} message
 */
export function setError(mount, message) {
  if (!mount) return;
  mount.hidden = !message;
  mount.replaceChildren(message ? document.createTextNode(message) : null);
}
